import { useEffect, useState } from 'react';
import { MenuItem, NavMenu } from '../nav-menu';
import { useAppRouter } from '@/routing';

// Component for mobile menu items that can be nested
export function MobileMenuItemComponent({
  item,
  isLast,
  level = 1,
  isExpanded,
  onToggle
}: {
  item: any;
  isLast?: boolean;
  level: number;
  isExpanded: boolean;
  onToggle: (title: string) => void;
}) {
  const router = useAppRouter();
  const [expandedNestedItem, setExpandedNestedItem] = useState<string | null>(null);
  const hasSubItems = item.items && item.items.length > 0;
  const paddingLeft = `${level * 0.75}rem`;

  useEffect(() => {
    if (!isExpanded) {
      setExpandedNestedItem(null);
    }
  }, [isExpanded]);

  return (
    <div className={`${isLast ? '' : 'border-b border-main-foreground/10'}`}>
      <div
        className={`flex min-h-[var(--erp-mobile-menu-item-height)] cursor-pointer items-center justify-between px-4 py-1.5 text-main-foreground transition-all duration-150 active:bg-main-foreground/10 ${
          isExpanded ? 'bg-main-foreground/20' : 'bg-main-foreground/5 hover:bg-main-foreground/10'
        }`}
        style={{ paddingLeft: `calc(1rem + ${paddingLeft})` }}
        onClick={() => {
          if (item.href) {
            router.push(item.href);
          } else {
            onToggle(item.title);
          }
        }}
      >
        <div className='flex items-center gap-2.5'>
          {item.icon && (
            <span className='flex h-[16px] w-[16px] shrink-0 items-center justify-center opacity-85 [&>*]:block [&>*]:size-[16px] [&>*]:shrink-0'>
              {item.icon}
            </span>
          )}
          <span className='text-xs font-medium tracking-wide'>{item.title}</span>
        </div>

        {hasSubItems && (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className={`h-3 w-3 opacity-60 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
          </svg>
        )}
      </div>

      {isExpanded && hasSubItems && (
        <div className='bg-black/15'>
          {item.items.map((subItem: any, idx: number) => (
            <MobileMenuItemComponent
              key={subItem.title}
              item={subItem}
              isLast={idx === item.items.length - 1}
              level={level + 1}
              isExpanded={expandedNestedItem === subItem.title}
              onToggle={title => {
                if (title === expandedNestedItem) {
                  setExpandedNestedItem(null);
                } else {
                  setExpandedNestedItem(title);
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Container for mobile navigation sections
export function MobileNavMenu({
  menu,
  isLast,
  isOpen,
  onToggle
}: {
  menu: NavMenu;
  isLast?: boolean;
  isOpen: boolean;
  onToggle: (title: string) => void;
}) {
  const [expandedSubItem, setExpandedSubItem] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setExpandedSubItem(null);
    }
  }, [isOpen]);

  // Group items by their group property
  const groupedItems: { [key: number]: MenuItem[] } = {};
  menu.items.forEach(item => {
    const group = item.group || 0;
    if (!groupedItems[group]) {
      groupedItems[group] = [];
    }
    groupedItems[group].push(item);
  });

  // Get sorted group keys
  const groupKeys = Object.keys(groupedItems).map(Number).sort();

  return (
    <div className={`border-b ${isLast ? 'border-transparent' : 'border-main-foreground/15'}`}>
      <button
        className={`flex min-h-[var(--erp-mobile-menu-section-height)] w-full items-center justify-between px-4 py-1.5 text-main-foreground transition-all duration-150 active:bg-main-foreground/10 ${
          isOpen ? 'bg-main-foreground/20 font-semibold text-main-foreground' : 'bg-main hover:bg-main-foreground/10'
        }`}
        onClick={() => onToggle(menu.title)}
      >
        <span className='text-sm tracking-wide'>{menu.title}</span>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className={`h-4 w-4 opacity-70 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
        </svg>
      </button>

      {isOpen && (
        <div className='bg-black/10 transition-all duration-200'>
          {groupKeys.map((group, groupIndex) => (
            <div key={group}>
              {groupIndex > 0 && <div className='mx-4 border-t border-main-foreground/10'></div>}
              {groupedItems[group].map((item, idx) => (
                <MobileMenuItemComponent
                  key={item.title}
                  item={item}
                  isLast={idx === groupedItems[group].length - 1 && groupIndex === groupKeys.length - 1}
                  level={1}
                  isExpanded={expandedSubItem === item.title}
                  onToggle={title => {
                    if (title === expandedSubItem) {
                      setExpandedSubItem(null);
                    } else {
                      setExpandedSubItem(title);
                    }
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
